import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { getSupplierById } from '../../services/supplierService';
import { addPartToSupplier, addUpcToSupplierPart } from '../../services/supplierService';

// Material UI components
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  AlertTitle,
  Breadcrumbs,
  Link,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';

// Material UI icons
import {
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Search as SearchIcon
} from '@mui/icons-material';

const AddPartToSupplier = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [formData, setFormData] = useState({
    part_id: '',
    supplier_part_number: '',
    cost: '',
    lead_time_days: '',
    url: '',
    upc_code: ''
  });

  const [selectedPart, setSelectedPart] = useState(null);

  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        setLoading(true);
        setError(null);

        const supplierData = await getSupplierById(id);
        setSupplier(supplierData);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching supplier:', err);
        setError(err.message || 'Failed to load supplier details');
        setLoading(false);
      }
    };

    fetchSupplier();
  }, [id]);

  const handleSearchParts = async () => {
    if (!searchTerm.trim()) return;

    try {
      setSearching(true);

      // In a real implementation, you would call an API endpoint to search parts
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/parts?search=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'x-auth-token': token
        }
      });
      const data = await response.json();

      setSearchResults(data);
      setSearching(false);
    } catch (err) {
      console.error('Error searching parts:', err);
      setSearching(false);
    }
  };

  const handlePartSelect = async (part) => {
    if (!part) {
      setSelectedPart(null);
      setFormData({
        ...formData,
        part_id: ''
      });
      return;
    }

    setSelectedPart(part);
    setFormData({
      ...formData,
      part_id: part.id
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.part_id) {
      setError('Please select a part');
      return;
    }

    if (!formData.supplier_part_number.trim()) {
      setError('Supplier part number is required');
      return;
    }

    if (!formData.cost || parseFloat(formData.cost) <= 0) {
      setError('Cost must be a positive number');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Add the part to the supplier
      const newSupplierPart = await addPartToSupplier(id, formData.part_id, {
        supplier_part_number: formData.supplier_part_number,
        cost: parseFloat(formData.cost),
        lead_time_days: formData.lead_time_days ? parseInt(formData.lead_time_days) : null,
        url: formData.url || null
      });

      // If UPC code is provided, add it to the supplier part
      if (formData.upc_code && formData.upc_code.trim()) {
        await addUpcToSupplierPart(id, formData.part_id, {
          upc_code: formData.upc_code.trim(),
          is_primary: true // Set as primary since it's the first UPC code
        });
      }

      // Redirect to supplier details on success
      navigate(`/suppliers/${id}`, {
        state: {
          notification: {
            message: 'Part added to supplier successfully',
            severity: 'success'
          }
        }
      });
    } catch (err) {
      console.error('Error adding part to supplier:', err);
      setError(err.message || 'Failed to add part to supplier. Please try again.');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!supplier) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          Supplier not found
        </Alert>
        <Button
          variant="outlined"
          component={RouterLink}
          to="/suppliers"
        >
          Back to Suppliers
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </Link>
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to="/suppliers"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Suppliers
        </Link>
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to={`/suppliers/${id}`}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          Supplier Details
        </Link>
        <Typography
          color="text.primary"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          Add Part
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <InventoryIcon sx={{ mr: 2, color: 'primary.main' }} />
        <Typography
          variant="h4"
          pb='0'
          mb='0'
        >
          Add Part to {supplier.name}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search for Part
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label="Search Parts"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              helperText="Search by part number or description"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searching ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton
                        onClick={handleSearchParts}
                        disabled={!searchTerm.trim()}
                        edge="end"
                      >
                        <SearchIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchParts();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearchParts}
              disabled={searching || !searchTerm.trim()}
              sx={{ height: 56 }}
            >
              Search
            </Button>
          </Box>

          {searchResults.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Search Results
              </Typography>
              <Autocomplete
                options={searchResults}
                getOptionLabel={(option) => `${option.internal_part_number} - ${option.description}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select a Part"
                    variant="outlined"
                    helperText="Choose a part from the search results"
                  />
                )}
                onChange={(event, newValue) => {
                  handlePartSelect(newValue);
                }}
                fullWidth
              />
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Part Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Supplier Part Number"
                name="supplier_part_number"
                value={formData.supplier_part_number}
                onChange={handleChange}
                variant="outlined"
                helperText="Enter the part number used by this supplier"
                disabled={!selectedPart}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Cost"
                name="cost"
                type="number"
                inputProps={{ min: "0.01", step: "0.01" }}
                value={formData.cost}
                onChange={handleChange}
                variant="outlined"
                helperText="Cost of the part from this supplier"
                disabled={!selectedPart}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Lead Time (Days)"
                name="lead_time_days"
                type="number"
                inputProps={{ min: "0", step: "1" }}
                value={formData.lead_time_days}
                onChange={handleChange}
                variant="outlined"
                helperText="Typical lead time in days (optional)"
                disabled={!selectedPart}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Part URL (Optional)"
                name="url"
                type="url"
                value={formData.url}
                onChange={handleChange}
                variant="outlined"
                helperText="URL to the part on the supplier's website"
                disabled={!selectedPart}
                placeholder="https://supplier-website.com/part/123"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="UPC Code (Optional)"
                name="upc_code"
                value={formData.upc_code}
                onChange={handleChange}
                variant="outlined"
                helperText="UPC barcode for this part (can add more later)"
                disabled={!selectedPart}
              />
            </Grid>
          </Grid>

        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CancelIcon />}
            component={RouterLink}
            to={`/suppliers/${id}`}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
            disabled={saving || !selectedPart}
          >
            {saving ? 'Saving...' : 'Add Part to Supplier'}
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default AddPartToSupplier;
